import { useMemo } from 'react';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import { distinctArray } from '@elementary/common-ui/utils/common';
import { CATALOG_TABLE_ASSETS_FILTER_OPTIONS } from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import {
  CATALOG_TABLE_FILTERS,
  CATALOG_TABLE_SORT,
} from '@elementary/common-ui/constants/urlParams';
import {
  mapUrlFiltersToGraphqlFilters,
  parseGraphqlFilterOptions,
} from '@elementary/common-ui/utils/graphqlFilters';
import {
  CatalogAssetsFilterOptionsResponse,
  getCatalogAssetsFilterOptionsQuery,
} from '../api';
import { COLUMN_TO_FIELD_MAP, FIELD_TO_COLUMN_MAP } from '../consts';

export const useCatalogTableQueryContext = () => {
  const selectedEnvId = useSelectedEnv();
  const [searchParams] = useSearchParams();

  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();

  const selectedFolderAssetIds = useMemo(() => {
    const assetIds = selectedTreeFolderNodes
      ?.filter((node) => node.resourceType !== 'test')
      .map((node) => node.id);
    return assetIds ? distinctArray(assetIds) : undefined;
  }, [selectedTreeFolderNodes]);

  const selectedFolderAssetIdsHash = useMemo(
    () => (selectedFolderAssetIds ? hashArray(selectedFolderAssetIds) : null),
    [selectedFolderAssetIds],
  );

  const hasSelectedNodes = useMemo(
    () => !!(selectedTreeFolderNodes?.length || selectedTreeNode?.id),
    [selectedTreeFolderNodes, selectedTreeNode],
  );

  const { urlFiltersDebounced } = useUrlFilters({
    urlKey: CATALOG_TABLE_FILTERS,
  });

  const urlSort: { id: string; direction: 'asc' | 'desc' } | null = useMemo(
    () => JSON.parse(searchParams.get(CATALOG_TABLE_SORT) || 'null') || null,
    [searchParams],
  );

  const assetFilters = useMemo(
    () =>
      mapUrlFiltersToGraphqlFilters(urlFiltersDebounced, COLUMN_TO_FIELD_MAP),
    [urlFiltersDebounced],
  );

  const sort = useMemo(() => {
    if (!urlSort) return undefined;
    const field = COLUMN_TO_FIELD_MAP[urlSort.id];
    if (!field) return undefined;
    return {
      field,
      direction: urlSort.direction.toUpperCase() as 'ASC' | 'DESC',
    };
  }, [urlSort]);

  const {
    data: filterOptionsData,
    isFetching: isFilterOptionsFetching,
    isError: isFilterOptionsError,
  } = useGraphqlQuery<CatalogAssetsFilterOptionsResponse>({
    query: getCatalogAssetsFilterOptionsQuery,
    variables: {
      filters: {
        ids: selectedFolderAssetIds?.length ? selectedFolderAssetIds : null,
      },
    },
    queryKey: [
      CATALOG_TABLE_ASSETS_FILTER_OPTIONS,
      selectedEnvId,
      selectedFolderAssetIdsHash,
    ],
    enabled: hasSelectedNodes,
    envId: selectedEnvId,
  });

  const filterOptions = useMemo(
    () =>
      parseGraphqlFilterOptions(
        filterOptionsData?.assetsFilterOptions || [],
        FIELD_TO_COLUMN_MAP,
      ),
    [filterOptionsData],
  );

  return {
    selectedEnvId,
    selectedFolderAssetIds,
    selectedFolderAssetIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    urlSort,
    assetFilters,
    sort,
    filterOptions,
    isFilterOptionsFetching,
    isFilterOptionsError,
  };
};
